import React, { ComponentType } from 'react';
import { NavLink, NavLinkProps, useHistory } from 'react-router-dom';
import { Trans } from 'react-i18next';

import { combineClassNames } from '@utils/combineClassNames';
import Icon from '../../icon';
import Badge from '@common/components/badge';
import { EPerformanceFormStatus, EStatus } from '@common/definitions';
import { EPulseSurveyStatus } from '@modules/survey/types/objects';
import { ESurveyStatus } from '@modules/survey/definitions';
import Spinner from '@common/components/spinner';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@common/components/ui/breadcrumb';
import Skeleton from '@modules/performance/components/skeleton/skeleton';

export const LOADING_BREADCRUMB = 'loading...';
export const LOADING_BREADCRUMB_SKELETON = 'skeleton...';

interface Tab extends NavLinkProps {
  name: string;
  to: string;
  count?: number;
  exact?: boolean;
}

export type BreadcrumbData = {
  name: string | React.ReactNode;
  path?: string;
  onClick?: () => void;
  mask?: boolean;
};

type Props = {
  className?: string;
  children?: JSX.Element;
  title?: React.ReactNode;
  count?: number;
  image?: string;
  badge?: EStatus | EPulseSurveyStatus | ESurveyStatus | EPerformanceFormStatus;
  form?: ComponentType<any>;
  formProps?: Record<string, unknown>;
  breadcrumbs?: (BreadcrumbData | undefined)[];
  description?: string | React.ReactNode;
  learnMoreHref?: string;
  learnMoreText?: React.ReactNode;
  searchbar?: JSX.Element,
  action?: unknown;
  progress?: number;
  mask?: boolean;
  tabs?: (Tab | undefined)[];
  employeeDetails?: React.ReactNode;
  fullWidth?: boolean;
};

type DefaultFormProps = { children: JSX.Element };
const DefaultForm = ({ children }: DefaultFormProps) => children;

export const TopNavigationBar = ({
  className,
  children,
  form: Form,
  formProps,
  image,
  badge,
  title,
  count,
  breadcrumbs,
  description,
  learnMoreHref,
  learnMoreText,
  searchbar,
  action,
  tabs,
  employeeDetails,
  progress,
  mask,
  fullWidth,
}: Props) => {
  const history = useHistory();
  const FormComponent = Form || DefaultForm;

  const actualBreadcrumbs = (breadcrumbs && breadcrumbs.filter((b) => !!b)) as (undefined | BreadcrumbData[]);

  const containerClassName = combineClassNames('Container__Content', 'TopNavigationBar__Container', {
    'TopNavigationBar__Container--FullWidth': fullWidth,
  });

  return (
    <div className={combineClassNames('TopNavigationBar', className)}>
      {actualBreadcrumbs && (
        <div className="TopNavigationBar__Breadcrumb">
          <div className="Container__Content">
            <Breadcrumb>
              <BreadcrumbList className="">
                {actualBreadcrumbs.map((item, i) => {
                  const isLast = i === actualBreadcrumbs.length - 1;

                  if (item.name === LOADING_BREADCRUMB) {
                    return <Spinner />;
                  }

                  if (item.name === LOADING_BREADCRUMB_SKELETON) {
                    return <Skeleton height={16} width={180} />;
                  }

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={item.path || (item.name as string)}>
                      <BreadcrumbItem>
                        {isLast ? (
                          <BreadcrumbPage className={combineClassNames({ 'fs-mask': !!item.mask, 'tw-font-semibold': isLast })}>
                            {item.name}
                          </BreadcrumbPage>
                        ) : (
                          <>
                            {item.path || item.onClick ? (
                              <BreadcrumbLink
                                asChild
                                className={combineClassNames({ 'fs-mask': !!item.mask })}
                              >
                                <a
                                  className="tw-text-inherit"
                                  onClick={item.onClick || (() => item.path && history.push(item.path))}
                                >
                                  {item.name}
                                </a>
                              </BreadcrumbLink>
                            ) : (
                              <BreadcrumbPage
                                className={combineClassNames({ 'fs-mask': !!item.mask, 'tw-text-gray-custom-500': !isLast })}
                              >
                                {item.name}
                              </BreadcrumbPage>
                            )}
                          </>
                        )}
                      </BreadcrumbItem>
                      {!isLast && <BreadcrumbSeparator />}
                    </React.Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
      )}
      <div className={containerClassName}>
        <div className="TopNavigationBar__Row">
          {(title || description) ? (
            <FormComponent {...formProps}>
              <div className={`TopNavigationBar__Row__Info${Form ? ' TopNavigationBar__Row__Info--form' : ''}`}>
                <div className="Align Align--no-wrap Align--center">
                  {image !== undefined && (
                    <div className="TopNavigationBar__Row__Info__Image fs-exclude">
                      {image
                        // @ts-expect-error
                        ? <img src={image} alt={title} />
                        : <Icon type="image" />}
                    </div>
                  )}
                  <h2 className={combineClassNames({ 'fs-mask': !!mask })}>
                    {title}
                    {count !== undefined && <span className="TopNavigationBar__Count">{` (${count})`}</span>}
                    {learnMoreHref && (
                      <a href={learnMoreHref} target="_blank" rel="noreferrer">
                        <Trans i18nKey="core:learn_more" />
                      </a>
                    )}
                    {learnMoreText && learnMoreText}
                  </h2>
                  {badge && <Badge status={badge} />}
                </div>
                {description && <div className="TopNavigationBar__Row__Info__Description">{description}</div>}
              </div>
            </FormComponent>
          ) : <div className="TopNavigationBar__Row__Info" />}
          {searchbar && <div className="TopNavigationBar__SearchBar">{searchbar}</div>}
          {action}
        </div>
        {children}
        {tabs && (
          <div className="TopNavigationBar__Tabs">
            {tabs
              .reduce<React.ReactNode[]>((acc, tab) => {
              if (!tab) return acc;

              const { to, name, count: tabCount, ...props } = tab;

              return [
                ...acc,
                <NavLink
                  key={to}
                  to={to}
                  {...props}
                  className="TopNavigationBar__Tabs__Item"
                  activeClassName="TopNavigationBar__Tabs__Item--active"
                >
                  {name}
                  {tabCount !== undefined && ` ${tabCount}`}
                </NavLink>,
              ];
            }, [])}
          </div>
        )}
        {employeeDetails}
        {(
          typeof progress === 'number' &&
          progress === progress // NaN check
        ) && (
          <div className="TopNavigationBar__Progress">
            {`${progress}%`}
            <div className="TopNavigationBar__Progress__Bar">
              <div className="TopNavigationBar__Progress__Bar__Fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
